import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Search, Plus, Database } from 'lucide-react';
import { CredentialCard } from './CredentialCard';
import {
  ApiCredential,
  CreateApiCredentialData,
  getApiCredentials,
  createApiCredential,
  updateApiCredential,
  deleteApiCredential,
  testApiCredential
} from '../../../auth/services/credentials.service';

interface CredentialsManagerProps {
  onSelectCredential?: (credential: ApiCredential | null) => void;
  selectedCredentialId?: string;
  mode?: 'select' | 'manage';
}

export const CredentialsManager: React.FC<CredentialsManagerProps> = ({
  onSelectCredential,
  selectedCredentialId,
  mode = 'manage'
}) => {
  const [credentials, setCredentials] = useState<ApiCredential[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingCredential, setEditingCredential] = useState<ApiCredential | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const loadCredentials = async () => {
    try {
      setLoading(true);
      const data = await getApiCredentials();
      setCredentials(data);
    } catch (error: any) {
      console.error('Error loading credentials:', error.message);
      toast.error(error.message || 'Error cargando credenciales');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCredentials();
  }, []);

  const handleTest = async (credential: ApiCredential) => {
    try {
      const result = await testApiCredential(credential.id);
      toast.success(`Credencial ${credential.name} probada exitosamente`);
    } catch (error: any) {
      toast.error(`Error probando credencial: ${error.message}`);
    }
  };

  const handleDelete = async (credential: ApiCredential) => {
    if (!confirm(`¿Estás seguro de eliminar la credencial "${credential.name}"?`)) {
      return;
    }

    try {
      await deleteApiCredential(credential.id);
      toast.success('Credencial eliminada exitosamente');
      loadCredentials();
    } catch (error: any) {
      toast.error(`Error eliminando credencial: ${error.message}`);
    }
  };

  const getAuthTypeLabel = (authType: string) => {
    switch (authType) {
      case 'TOKEN_SIMPLE': return 'Token Simple';
      case 'BEARER_TOKEN': return 'Bearer Token';
      case 'OAUTH_CLIENT_CREDENTIALS': return 'OAuth 2.0';
      default: return authType;
    }
  };

  // Filtrar credenciales por búsqueda
  const filteredCredentials = credentials.filter((cred) =>
    cred.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    getAuthTypeLabel(cred.authType).toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-slate-400">Cargando credenciales...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con búsqueda y contador */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Contador */}
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-500/10 rounded-lg">
              <Database className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">
                {filteredCredentials.length}
              </p>
              <p className="text-sm text-slate-400">
                {filteredCredentials.length === 1 ? 'Credencial activa' : 'Credenciales activas'}
              </p>
            </div>
          </div>

          {/* Búsqueda y botón */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar credenciales..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="
                  w-full pl-10 pr-4 py-2.5 
                  bg-slate-900 border border-slate-600 rounded-lg
                  text-white placeholder-slate-400
                  focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500
                  transition-colors
                "
              />
            </div>
            {mode === 'manage' && (
              <button
                onClick={() => setShowCreateForm(true)}
                className="
                  flex items-center justify-center gap-2 px-5 py-2.5
                  bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg
                  transition-colors whitespace-nowrap
                "
              >
                <Plus className="w-4 h-4" />
                Nueva Credencial
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Opción "Sin Credencial" en modo select */}
      {mode === 'select' && (
        <div
          onClick={() => onSelectCredential?.(null)}
          className={`
            p-5 rounded-lg border-2 cursor-pointer transition-all duration-200
            hover:border-blue-500/50 hover:shadow-lg
            ${
              !selectedCredentialId
                ? 'border-blue-500 bg-blue-500/10 shadow-lg shadow-blue-500/20'
                : 'border-slate-700 bg-slate-800'
            }
          `}
        >
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-white text-lg mb-1">
                Sin Credencial (API Pública)
              </h4>
              <p className="text-sm text-slate-400">
                API sin autenticación requerida
              </p>
            </div>
            {!selectedCredentialId && (
              <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                <div className="w-2.5 h-2.5 bg-white rounded-full"></div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Grid de Credenciales */}
      {filteredCredentials.length === 0 ? (
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-12 text-center">
          {searchQuery ? (
            <>
              <Search className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 text-lg mb-2">No se encontraron credenciales</p>
              <p className="text-slate-500 text-sm">
                Intenta con otros términos de búsqueda
              </p>
            </>
          ) : (
            <>
              <Database className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 text-lg mb-2">
                No hay credenciales configuradas
              </p>
              {mode === 'manage' && (
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="mt-4 text-blue-400 hover:text-blue-300 font-medium"
                >
                  Crear primera credencial →
                </button>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {filteredCredentials.map((credential) => (
            <CredentialCard
              key={credential.id}
              credential={credential}
              onTest={handleTest}
              onEdit={(cred) => setEditingCredential(cred)}
              onDelete={handleDelete}
              isSelected={mode === 'select' && selectedCredentialId === credential.id}
              onSelect={mode === 'select' ? onSelectCredential : undefined}
              mode={mode}
            />
          ))}
        </div>
      )}

      {/* Formularios de Crear/Editar */}
      {(showCreateForm || editingCredential) && (
        <CredentialFormModal
          credential={editingCredential}
          onClose={() => {
            setShowCreateForm(false);
            setEditingCredential(null);
          }}
          onSave={() => {
            setShowCreateForm(false);
            setEditingCredential(null);
            loadCredentials();
          }}
        />
      )}
    </div>
  );
};

interface CredentialFormModalProps {
  credential?: ApiCredential | null;
  onClose: () => void;
  onSave: () => void;
}

const CredentialFormModal: React.FC<CredentialFormModalProps> = ({
  credential,
  onClose,
  onSave
}) => {
  const isEditing = !!credential;
  
  const [formData, setFormData] = useState<CreateApiCredentialData>({
    name: credential?.name || '',
    authType: credential?.authType || 'TOKEN_SIMPLE',
    // Cargar metadata existente (no secretos)
    tokenUrl: credential?.tokenUrl || '',
    tokenUrlOAuth: credential?.tokenUrlOAuth || '',
    tokenMethod: credential?.tokenMethod || 'POST',
    scope: credential?.scope || ''
    // Los secretos (clientId, clientSecret, bearerToken) se dejan vacíos
    // El usuario debe re-ingresarlos si quiere cambiarlos
  });
  const [tokenBodyText, setTokenBodyText] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    // Validar campos requeridos según tipo de auth
    if (formData.authType === 'TOKEN_SIMPLE') {
      if (!isEditing && !formData.tokenUrl) {
        toast.error('URL del token es requerida para TOKEN_SIMPLE');
        return;
      }
    }

    if (formData.authType === 'OAUTH_CLIENT_CREDENTIALS') {
      // En modo creación: requerir todo
      // En modo edición: solo requerir si se está cambiando
      if (!isEditing) {
        if (!formData.clientId || !formData.clientSecret) {
          toast.error('Client ID y Client Secret son requeridos para OAuth');
          return;
        }
        if (!formData.tokenUrlOAuth) {
          toast.error('Token URL es requerida para OAuth');
          return;
        }
      } else {
        // En edición: validar que si se cambió uno, se cambió el otro
        const hasClientId = formData.clientId || credential?.hasClientId;
        const hasClientSecret = formData.clientSecret || credential?.hasClientSecret;
        const hasTokenUrl = formData.tokenUrlOAuth || credential?.tokenUrlOAuth;
        
        if (!hasClientId || !hasClientSecret || !hasTokenUrl) {
          toast.error('Client ID, Client Secret y Token URL son requeridos');
          return;
        }
      }
    }

    if (formData.authType === 'BEARER_TOKEN') {
      if (!isEditing && !formData.bearerToken) {
        toast.error('Bearer Token es requerido');
        return;
      }
    }

    let parsedTokenBody: Record<string, any> | undefined;
    if (formData.authType === 'TOKEN_SIMPLE' && tokenBodyText.trim()) {
      try {
        const parsed = JSON.parse(tokenBodyText);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          toast.error('El body debe ser un objeto JSON');
          return;
        }
        parsedTokenBody = parsed as Record<string, any>;
      } catch (error) {
        toast.error('El body debe ser un JSON válido');
        return;
      }
    }

    const payload: CreateApiCredentialData = {
      ...formData,
      ...(parsedTokenBody ? { tokenBody: parsedTokenBody } : {})
    };

    try {
      setLoading(true);
      
      if (credential) {
        await updateApiCredential(credential.id, payload);
        toast.success('Credencial actualizada exitosamente');
      } else {
        await createApiCredential(payload);
        toast.success('Credencial creada exitosamente');
      }
      
      onSave();
    } catch (error: any) {
      toast.error(error.message || 'Error guardando credencial');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-slate-700 flex-shrink-0">
          <h3 className="text-lg font-semibold text-white">
            {credential ? 'Editar Credencial' : 'Nueva Credencial'}
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Nombre */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Nombre de la Credencial
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white"
              placeholder="ej: Finnegans API"
              required
            />
          </div>

          {/* Tipo de Autenticación */}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Tipo de Autenticación
            </label>
            <select
              value={formData.authType}
              onChange={(e) => setFormData({ ...formData, authType: e.target.value as any })}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white"
            >
              <option value="TOKEN_SIMPLE">Token Simple</option>
              <option value="BEARER_TOKEN">Bearer Token</option>
              <option value="OAUTH_CLIENT_CREDENTIALS">OAuth 2.0</option>
            </select>
          </div>

          {/* Campos específicos según tipo */}
          {formData.authType === 'TOKEN_SIMPLE' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  URL del Token
                </label>
                <input
                  type="url"
                  value={formData.tokenUrl || ''}
                  onChange={(e) => setFormData({ ...formData, tokenUrl: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white"
                  placeholder="https://api.ejemplo.com/auth/token"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Método HTTP
                </label>
                <select
                  value={formData.tokenMethod || 'POST'}
                  onChange={(e) => setFormData({ ...formData, tokenMethod: e.target.value as 'GET' | 'POST' })}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white"
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Body (JSON){' '}
                  {isEditing && credential?.hasTokenBody && (
                    <span className="text-xs text-green-400">(configurado)</span>
                  )}
                </label>
                <textarea
                  value={tokenBodyText}
                  onChange={(e) => setTokenBodyText(e.target.value)}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white h-28 font-mono text-sm"
                  placeholder={isEditing && credential?.hasTokenBody
                    ? '{\n  "email": "usuario@dominio.com",\n  "password": "••••••••",\n  "domain": "EMPRESA"\n}\n(dejar vacío para no cambiar)'
                    : '{\n  "email": "usuario@dominio.com",\n  "password": "••••••••",\n  "domain": "EMPRESA"\n}'
                  }
                />
                <p className="text-xs text-slate-400 mt-2">
                  Ingresa un JSON válido para enviar como body en la autenticación Token Simple.
                </p>
              </div>
            </div>
          )}

          {formData.authType === 'OAUTH_CLIENT_CREDENTIALS' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Client ID {isEditing && credential?.hasClientId && <span className="text-xs text-green-400">(configurado)</span>}
                </label>
                <input
                  type="text"
                  value={formData.clientId || ''}
                  onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white"
                  placeholder={isEditing && credential?.hasClientId ? '•••••••••• (dejar vacío para no cambiar)' : 'Client ID'}
                  required={!isEditing}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Client Secret {isEditing && credential?.hasClientSecret && <span className="text-xs text-green-400">(configurado)</span>}
                </label>
                <input
                  type="password"
                  value={formData.clientSecret || ''}
                  onChange={(e) => setFormData({ ...formData, clientSecret: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white"
                  placeholder={isEditing && credential?.hasClientSecret ? '•••••••••• (dejar vacío para no cambiar)' : 'Client Secret'}
                  required={!isEditing}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Token URL *
                </label>
                <input
                  type="url"
                  value={formData.tokenUrlOAuth || ''}
                  onChange={(e) => setFormData({ ...formData, tokenUrlOAuth: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white"
                  placeholder="https://api.xubio.com/oauth/token"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Método HTTP
                </label>
                <select
                  value={formData.tokenMethod || 'POST'}
                  onChange={(e) => setFormData({ ...formData, tokenMethod: e.target.value as 'GET' | 'POST' })}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white"
                >
                  <option value="GET">GET (query params)</option>
                  <option value="POST">POST (Basic Auth)</option>
                </select>
                <p className="text-xs text-slate-400 mt-2">
                  GET enviará <code className="bg-slate-900 px-1 rounded">client_id</code>, <code className="bg-slate-900 px-1 rounded">client_secret</code> y <code className="bg-slate-900 px-1 rounded">grant_type</code> como query params.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Scope (opcional)
                </label>
                <input
                  type="text"
                  value={formData.scope || ''}
                  onChange={(e) => setFormData({ ...formData, scope: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white"
                  placeholder="read write"
                />
              </div>
            </div>
          )}

          {formData.authType === 'BEARER_TOKEN' && (
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Bearer Token
              </label>
              <textarea
                value={formData.bearerToken || ''}
                onChange={(e) => setFormData({ ...formData, bearerToken: e.target.value })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white h-24"
                placeholder="Pega aquí tu token..."
                required
              />
            </div>
          )}

          {/* Botones */}
          </div>
          <div className="flex gap-3 p-6 border-t border-slate-700 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Guardando...' : credential ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
